import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ThumbsUp, Flame, Sparkles, ChevronUp, Plus, ExternalLink, CheckCircle, MapPin, Megaphone, Info, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Generate or get voter ID from cookie
const getVoterId = () => {
  let voterId = localStorage.getItem("snack_voter_id");
  if (!voterId) {
    voterId = `voter_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem("snack_voter_id", voterId);
  }
  return voterId;
};

const PublicWishesPage = () => {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votedWishes, setVotedWishes] = useState(() => {
    const stored = localStorage.getItem("snack_voted_wishes");
    return stored ? JSON.parse(stored) : [];
  });
  const [votingId, setVotingId] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  const fetchWishes = useCallback(async () => {
    try {
      const [wishesRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/api/public/wishes`),
        axios.get(`${API}/api/public/wish-announcements`).catch(() => ({ data: [] }))
      ]);
      setWishes(wishesRes.data || []);
      setAnnouncements(announcementsRes.data || []);
    } catch (err) {
      console.error("Error fetching wishes:", err);
      toast.error("Fehler beim Laden der Wünsche");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWishes();
  }, [fetchWishes]);

  const handleVote = async (wishId) => {
    if (votedWishes.includes(wishId)) {
      toast.info("Du hast bereits für diesen Wunsch gestimmt");
      return;
    }

    setVotingId(wishId);
    try {
      const voterId = getVoterId();
      const res = await axios.post(`${API}/api/public/wishes/${wishId}/vote?voter_id=${encodeURIComponent(voterId)}`);
      
      if (res.data.success) {
        toast.success("Danke für deine Stimme!");
        
        // Update local state
        const newVotedWishes = [...votedWishes, wishId];
        setVotedWishes(newVotedWishes);
        localStorage.setItem("snack_voted_wishes", JSON.stringify(newVotedWishes));
        
        // Update wish in list
        setWishes(prev => prev.map(w => 
          w.id === wishId ? { ...w, votes: res.data.wish.votes } : w
        ).sort((a, b) => (b.votes || 0) - (a.votes || 0)));
      }
    } catch (err) {
      if (err.response?.status === 400) {
        // Already voted - sync local state
        const newVotedWishes = [...votedWishes, wishId];
        setVotedWishes(newVotedWishes);
        localStorage.setItem("snack_voted_wishes", JSON.stringify(newVotedWishes));
        toast.info("Du hast bereits für diesen Wunsch gestimmt");
      } else {
        toast.error(err.response?.data?.detail || "Fehler beim Abstimmen");
      }
    } finally {
      setVotingId(null);
    }
  };

  const hasVoted = (wishId) => votedWishes.includes(wishId);

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-orange-500 text-white py-6 px-4 shadow-lg">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Flame className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Produktwünsche</h1>
          </div>
          <p className="text-rose-100 text-sm">
            Feuerwehr Lüneburg Mitte - Snackautomat
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="space-y-3 mb-6">
            {announcements.map((announcement) => (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`
                  border text-white
                  ${announcement.type === 'success' ? 'bg-emerald-900/50 border-emerald-600' :
                    announcement.type === 'warning' ? 'bg-amber-900/50 border-amber-600' :
                    'bg-blue-900/50 border-blue-600'}
                `}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className={`
                        p-2 rounded-lg
                        ${announcement.type === 'success' ? 'bg-emerald-500/20' :
                          announcement.type === 'warning' ? 'bg-amber-500/20' :
                          'bg-blue-500/20'}
                      `}>
                        {announcement.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : announcement.type === 'warning' ? (
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                        ) : (
                          <Megaphone className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        {announcement.title && (
                          <h3 className="font-semibold text-white mb-1">{announcement.title}</h3>
                        )}
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">
                          {announcement.message}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="mb-6 bg-slate-800/50 border-slate-700 text-white">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-300">
                  Stimme für deine Lieblingswünsche! Je mehr Stimmen ein Produkt hat, 
                  desto wahrscheinlicher kommt es in den Automaten.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wishes List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-rose-500 border-t-transparent"></div>
          </div>
        ) : wishes.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700 text-white text-center py-12">
            <CardContent>
              <Heart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">Noch keine Wünsche vorhanden</p>
              <Link to="/wunsch">
                <Button className="bg-rose-600 hover:bg-rose-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Wunsch einreichen
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Fulfilled Wishes Section */}
            {wishes.some(w => w.display_status === "fulfilled") && (
              <div className="mb-6">
                <h2 className="text-emerald-400 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Erfüllte Wünsche
                </h2>
                <div className="space-y-2">
                  {wishes.filter(w => w.display_status === "fulfilled").map((wish) => (
                    <motion.div
                      key={wish.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card className="bg-emerald-900/30 border-emerald-700/50">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                                <CheckCircle className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-emerald-100 truncate">
                                  {wish.product_name}
                                </h3>
                                {wish.assigned_slots && wish.assigned_slots.length > 0 && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <MapPin className="w-3 h-3 text-emerald-400" />
                                    <span className="text-xs text-emerald-300">
                                      Verfügbar in Fach: {wish.assigned_slots.join(", ")}
                                    </span>
                                  </div>
                                )}
                                {wish.admin_note && (
                                  <p className="text-xs text-emerald-400/70 mt-1">
                                    {wish.admin_note}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className="bg-emerald-600 text-white shrink-0">
                              Im Automaten
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Open Wishes Section */}
            {wishes.some(w => w.display_status === "open" || !w.display_status) && (
              <div>
                {wishes.some(w => w.display_status === "fulfilled") && (
                  <h2 className="text-slate-400 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Offene Wünsche - Jetzt abstimmen!
                  </h2>
                )}
                <AnimatePresence>
                  {wishes.filter(w => w.display_status === "open" || !w.display_status).map((wish, index) => (
                    <motion.div
                      key={wish.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.05 }}
                      className="mb-3"
                    >
                      <Card 
                        className={`
                          bg-slate-800/80 border-slate-700 overflow-hidden
                          ${index === 0 ? 'ring-2 ring-amber-500/50' : ''}
                          ${hasVoted(wish.id) ? 'border-emerald-500/50' : ''}
                        `}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            {/* Rank & Product Info */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Rank Badge */}
                              <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                                ${index === 0 ? 'bg-amber-500 text-amber-900' : 
                                  index === 1 ? 'bg-slate-400 text-slate-900' : 
                                  index === 2 ? 'bg-orange-700 text-orange-100' : 
                                  'bg-slate-700 text-slate-400'}
                              `}>
                                {index + 1}
                              </div>
                              
                              {/* Product Details */}
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-white truncate">
                                  {wish.product_name}
                                </h3>
                                {wish.comment && (
                                  <p className="text-xs text-slate-400 truncate">
                                    {wish.comment}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Vote Button & Count */}
                            <div className="flex items-center gap-2 shrink-0">
                              {/* Vote Count */}
                              <div className="flex items-center gap-1 text-slate-400">
                                <Heart className={`w-4 h-4 ${hasVoted(wish.id) ? 'fill-rose-500 text-rose-500' : ''}`} />
                                <span className="font-semibold min-w-[20px] text-center">
                                  {wish.votes || 1}
                                </span>
                              </div>

                              {/* Vote Button */}
                              <Button
                                size="sm"
                                onClick={() => handleVote(wish.id)}
                                disabled={votingId === wish.id || hasVoted(wish.id)}
                                className={`
                                  min-w-[80px] transition-all
                                  ${hasVoted(wish.id) 
                                    ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600/30' 
                                    : 'bg-rose-600 hover:bg-rose-700 text-white'}
                                `}
                                data-testid={`vote-btn-${wish.id}`}
                              >
                                {votingId === wish.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                ) : hasVoted(wish.id) ? (
                                  <>
                                    <ThumbsUp className="w-4 h-4 mr-1" />
                                    Gewählt
                                  </>
                                ) : (
                                  <>
                                    <ChevronUp className="w-4 h-4 mr-1" />
                                    Vote
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}

        {/* Submit New Wish CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm mb-3">
            Dein Lieblingsprodukt ist nicht dabei?
          </p>
          <Link to="/wunsch">
            <Button variant="outline" className="border-rose-500/50 text-rose-400 hover:bg-rose-500/10">
              <Plus className="w-4 h-4 mr-2" />
              Neuen Wunsch einreichen
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur border-t border-slate-800 py-3 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between text-xs text-slate-500">
          <span>🔥 Feuerwehr Lüneburg Mitte</span>
          <Link 
            to="/wunsch" 
            className="flex items-center gap-1 text-rose-400 hover:text-rose-300"
          >
            <ExternalLink className="w-3 h-3" />
            Wunsch einreichen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicWishesPage;
